import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FilterContextType {
  selectedPortfolio: string;
  selectedPlatform: string;
  selectedApplication: string;
  selectedTimeRange: string;
  customDateRange: { start: string; end: string };
  customDateApplied: boolean;
  setSelectedPortfolio: (portfolio: string) => void;
  setSelectedPlatform: (platform: string) => void;
  setSelectedApplication: (application: string) => void;
  setSelectedTimeRange: (timeRange: string) => void;
  setCustomDateRange: (dateRange: { start: string; end: string }) => void;
  setCustomDateApplied: (applied: boolean) => void;
  resetFilters: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

const STORAGE_KEY = 'globalFilters';

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
  // Initialize from localStorage or use defaults
  const getInitialFilters = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedFilters = JSON.parse(stored);
        // Ensure we have 'all' as default, not empty strings
        return {
          ...parsedFilters,
          selectedPortfolio: parsedFilters.selectedPortfolio || 'all',
          selectedPlatform: parsedFilters.selectedPlatform || 'all',
        };
      }
    } catch (error) {
      console.error('Error reading filters from localStorage:', error);
    }
    return {
      selectedPortfolio: 'all',
      selectedPlatform: 'all',
      selectedApplication: 'all',
      selectedTimeRange: '30d',
      customDateRange: { start: '', end: '' },
      customDateApplied: false,
    };
  };

  const initialFilters = getInitialFilters();

  const [selectedPortfolio, setSelectedPortfolioState] = useState<string>(initialFilters.selectedPortfolio);
  const [selectedPlatform, setSelectedPlatformState] = useState<string>(initialFilters.selectedPlatform);
  const [selectedApplication, setSelectedApplicationState] = useState<string>(initialFilters.selectedApplication || 'all');
  const [selectedTimeRange, setSelectedTimeRangeState] = useState<string>(initialFilters.selectedTimeRange);
  const [customDateRange, setCustomDateRangeState] = useState<{ start: string; end: string }>(initialFilters.customDateRange);
  const [customDateApplied, setCustomDateAppliedState] = useState<boolean>(initialFilters.customDateApplied);

  // Save to localStorage whenever filters change
  useEffect(() => {
    const filters = {
      selectedPortfolio,
      selectedPlatform,
      selectedApplication,
      selectedTimeRange,
      customDateRange,
      customDateApplied,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
      console.log('🔄 Global filters updated:', filters);
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [selectedPortfolio, selectedPlatform, selectedApplication, selectedTimeRange, customDateRange, customDateApplied]);

  const setSelectedPortfolio = (portfolio: string) => {
    console.log('📊 Setting global portfolio:', portfolio);
    setSelectedPortfolioState(portfolio);
  };

  const setSelectedPlatform = (platform: string) => {
    console.log('🖥️ Setting global platform:', platform);
    setSelectedPlatformState(platform);
  };

  const setSelectedApplication = (application: string) => {
    console.log('📱 Setting global application:', application);
    setSelectedApplicationState(application);
  };

  const setSelectedTimeRange = (timeRange: string) => {
    console.log('📅 Setting global time range:', timeRange);
    setSelectedTimeRangeState(timeRange);
  };

  const setCustomDateRange = (dateRange: { start: string; end: string }) => {
    console.log('📆 Setting global custom date range:', dateRange);
    setCustomDateRangeState(dateRange);
  };

  const setCustomDateApplied = (applied: boolean) => {
    console.log('✅ Setting custom date applied:', applied);
    setCustomDateAppliedState(applied);
  };

  const resetFilters = () => {
    console.log('🔄 Resetting global filters to defaults');
    setSelectedPortfolioState('all');
    setSelectedPlatformState('all');
    setSelectedApplicationState('all');
    setSelectedTimeRangeState('30d');
    setCustomDateRangeState({ start: '', end: '' });
    setCustomDateAppliedState(false);
  };

  return (
    <FilterContext.Provider
      value={{
        selectedPortfolio,
        selectedPlatform,
        selectedApplication,
        selectedTimeRange,
        customDateRange,
        customDateApplied,
        setSelectedPortfolio,
        setSelectedPlatform,
        setSelectedApplication,
        setSelectedTimeRange,
        setCustomDateRange,
        setCustomDateApplied,
        resetFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useGlobalFilters = () => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useGlobalFilters must be used within a FilterProvider');
  }
  return context;
};
